import {
  Badge,
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import bg from "../@shared/assets/mydecksbg.png";
import NeoButton from "../components/NeoButton";
import {
  MdArrowBack,
  MdCancel,
  MdCheck,
  MdCompareArrows,
  MdDelete,
  MdMouse,
} from "react-icons/md";
import { MainContext } from "../App";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { useCardStore } from "../@shared/store/useCardsStore";
import {
  applyTurnBuffs,
  buildBuffMessage,
  parseCardEffect,
  shuffle,
} from "../@shared/game/deckEngine";
import PlayerPlatform from "../components/gamePlay/PlayerPlatform";
import DeckStack from "../components/gamePlay/DeckStack";
import CurrentPlayer from "../components/gamePlay/CurrentPlayer";
import { motion, useMotionValue, useTransform } from "framer-motion";
import InfinityCard from "../components/InfinityCard";
import SpinWheel from "../components/gamePlay/SpinWheel";
import { useRoomsStore } from "../@shared/store/useRoomsStore";
import { useRef } from "react";
import {
  addNewLogToQuestLogs,
  checkAndUpdateLevel,
  formatTime,
  getToastMessageFromMove,
  specialtyCardEffect,
} from "../@shared/utils/Utils";
import {
  updateDoc,
  doc,
  arrayUnion,
  serverTimestamp,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { update, getDatabase, ref } from "firebase/database";
import firebase, { db } from "../@shared/firebase/config";
import {
  getAvailableHuman,
  updateHandAndDeck,
  updatePlayerPresence,
} from "../@shared/utils/roomFunctions";
import TradeCardAnimation from "../components/gamePlay/TradeCardAnimation";
import SlapCardAnimation from "../components/gamePlay/SlapCardAnimation";
import FightAnimation from "../components/gamePlay/FightAnimation";
import ActionToast from "../components/gamePlay/ActionToast";
import { useTurnEngine } from "../@shared/game/useTurnEngine";
import {
  updatePlayerPoints,
  ELEMENTS,
  ELEMENT_EFFECTIVENESS,
  getElementalBonus,
} from "../@shared/utils/Utils";
import {
  ArrowBackIos,
  ArrowUpward,
  Check,
  DoNotDisturb,
  Sms,
  Menu,
  MenuRounded,
} from "@mui/icons-material";
import PlayerResultCard from "../components/gamePlay/PlayerResultCard";
import ChatDrawer from "../components/ChatDrawer";
import XPBreakdown from "../components/gamePlay/XPBreakdown";
import { useDeckAndHandListeners } from "../@shared/game/useDeckAndHandListeners";
import Toast from "../components/Toast";
import SettingsDrawer from "../components/SettingsDrawer";
import {
  getNextLivingPlayerIndex,
  simulateBotTurn,
} from "../@shared/game/botActions";

const GameBoardScreen = () => {
  const {
    Theme,
    navigate,
    message,
    setMessage,
    setInfo,
    setError,
    setSuccess,
    setGameScreenLoading,
  } = useContext(MainContext);
  const pathname = window.location.pathname;
  const roomId = pathname.split("/")[2];
  const { userData } = useAuthStore();
  const matchId = "match";
  const {
    matchData,
    subscribeToMatch,
    hasUnreadMessages,
    clearUnreadMessages,
    unreadCount,
    subscribeToChatThread,
    setChatDrawerOpen,
  } = useRoomsStore();

  const playerIds = matchData?.players?.map((p) => p.id) || [];
  useDeckAndHandListeners(roomId, playerIds);
  const { hands, decks } = useRoomsStore();
  const myHand = hands[userData?.id] || [];
  const myDeck = decks[userData?.id] || [];
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [players, setPlayers] = useState([]);

  const [selectedCard, setSelectedCard] = useState(null);
  const [cardLayout, setCardLayout] = useState(null);

  //Card preBox...
  const scale = useMotionValue(1);
  const translateX = useMotionValue(0);
  const translateY = useMotionValue(0);

  // PLAYER MOVE(s) STATE =============================================
  const [playerMove, setPlayerMove] = useState(true);
  const [showPlayerMenu, setShowPlayerMenu] = useState(false);
  const [playerMoveType, setPlayerMoveType] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [numberSpun, setNumberSpun] = useState(null);

  // TOAST AND STATE =========================================
  const [toastMessage, setToastMessage] = useState(null); // Can still keep this local
  const disabledByToast =
    (toastMessage !== null && toastMessage !== "") ||
    matchData?.winner !== null;

  const hasStartedBotTurnRef = useRef(false);

  //  ANIMATION STATE =========================================
  const [activeAnimation, setActiveAnimation] = useState(null); // 🎯 PlayerPlatform exclusive
  const [attackAnimation, setAttackAnimation] = useState(null); // 🎴 SlapCardAnimation exclusive
  const [activeCardPlayAnimation, setActiveCardPlayAnimation] = useState(null); // 🎴 SlapCardAnimation exclusive
  const [showTradeAnimation, setShowTradeAnimation] = useState(false);

  const [showSlapAnimation, setShowSlapAnimation] = useState(false);
  const [slapCardData, setSlapCardData] = useState(null);
  const [finishedDrawAnimation, setFinishedDrawAnimation] = useState(false);

  const lastAnimationTimestampRef = useRef(false);
  const hasProcessedTurnRef = useRef("");

  //Timer ==============================================================
  const lastMoveTimestamp =
    matchData?.moves?.[matchData.moves.length - 1]?.timestamp;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [ssecondsTimer, setSecondsTimer] = useState(30);
  const timerRef = useRef(null);
  const timeoutTriggeredRef = useRef(false);
  const turnLossRef = useRef(false);

  const levelUpHandledRef = useRef({});

  //Chat Screen =====================================================
  const [chatOpen, setChatOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  //Results Screen =====================================================
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultsPage, setResultsPage] = useState("placement"); // "placement" | "xp"
  const [allPlayersXP, setAllPlayersXP] = useState(null);
  const [playerPoints, setPlayerPoints] = useState(null);
  const [quitPressed, setQuitPressed] = useState(false);
  const hasHandledEndOfMatch = useRef(false);

  const cpMovesLeft = matchData?.cpMovesLeft ?? 0;

  //Move Logic =======================================================
  const matchRef = doc(db, "rooms", roomId, "matchData", matchId);

  const gatherAllPlayersXP = async () => {
    let xpData = {};

    for (const player of matchData.players) {
      const userRef = doc(db, "users", player.id);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        xpData[player.id] = {
          xp: userData.xp || 0, // however your XP field is named
          level: userData.level || 1, // if you also track levels
        };
      }
    }

    setAllPlayersXP(xpData);
    return xpData;
  };

  const refillHandIfNeeded = async (playerId) => {
    const hand = [...(hands[playerId] || [])];
    const deck = [...(decks[playerId] || [])];

    let cardsToDraw = Math.min(5 - hand.length, deck.length);
    for (let i = 0; i < cardsToDraw; i++) {
      hand.push(deck.shift());
      // Optionally add card draw animation here
    }

    await updateHandAndDeck(roomId, playerId, hand, deck);
  };

  const nextTurn = async (force = false) => {
    // const cpMovesLeft = matchData.cpMovesLeft ?? 0;
    const currentTurn = matchData.currentPlayerTurn;
    const currentPlayer = players[currentTurn];
    const human = await getAvailableHuman(players, roomId);

    console.log("⚙️ nextTurn CALLED", {
      cpMovesLeft,
      currentPlayerTurn: currentTurn,
      isUserTurn: currentPlayer?.id === userData.id,
    });
    if (!human || human.id !== userData.id) return;
    // if (!force && (cpMovesLeft > 0 || currentPlayer?.id !== userData.id))
    //   return;

    // if (cpMovesLeft > 0 || currentPlayer?.id !== userData.id) return;

    const nextIndex = getNextLivingPlayerIndex(players, currentTurn);
    const nextPlayer = players[nextIndex];
    const newTurnCount = (matchData.currentTurn ?? 0) + 1;

    await updateDoc(matchRef, {
      cpMovesLeft: 2,
      currentPlayerTurn: nextIndex,
      currentTurn: newTurnCount,
      message: `🌀 It's @${nextPlayer.name}'s turn!`,
    });

    console.log(`✅ Turn passed to: ${nextPlayer.name}`);
  };

  const syncMatchData = async (actingPlayerId, updatedPlayers, move = null) => {
    if (!matchId || !roomId || matchData.status === "complete") return;

    try {
      const latestSnap = await getDoc(matchRef);
      const latestData = latestSnap.data();
      const cpMovesLeft = latestData?.cpMovesLeft ?? 0;
      let UpdatedPlayers = [...updatedPlayers];
      let actingPlayer = UpdatedPlayers.find(
        (player) => player.id === actingPlayerId
      );
      let actingPlayerIndex = UpdatedPlayers.findIndex(
        (player) => player.id === actingPlayerId
      );

      console.log("DEBUG: Player has ", matchData?.cpMovesLeft, " moves left.");

      if (move) {
        let message = getToastMessageFromMove(move);
        console.log("DEBUG: message...", message);
        console.log("DEBUG: match ref", matchRef);

        let last25Moves = latestData?.moves?.slice(-25) || [];
        let newMovesArray = [...last25Moves, move];

        console.log("DEBUG: newMovesArray", newMovesArray);
        console.log("DEBUG: updatedPlayers", updatedPlayers?.length);
        console.log("DEBUG: message", message);
        console.log("DEBUG: cpMovesLeft", cpMovesLeft);

        if (cpMovesLeft - 1 === 0) {
          await refillHandIfNeeded(currentPlayer.id);
          //   actingPlayer.character = applyTurnBuffs(actingPlayer.character);
          UpdatedPlayers[actingPlayerIndex] = actingPlayer;
        }
        await updateDoc(matchRef, {
          players: UpdatedPlayers,
          moves: newMovesArray,
          message,
          cpMovesLeft: Math.max(cpMovesLeft - 1, 0),
          turnStartTime: new Date().getTime(),
        });
      }
    } catch (err) {
      console.error("❌ Failed to sync match data:", err);
    }
  };

  //   useTurnEngine({
  //     matchData,
  //     roomId,
  //     matchId,
  //     userData,
  //     players,
  //     syncMatchData,
  //     hands,
  //     decks,
  //   });

  const handleAttack = async () => {
    if (!currentPlayer || !selectedPlayer || numberSpun == null) return;

    const playersCopy = [...players];
    const opponentIndex = playersCopy.findIndex(
      (p) => p.id === selectedPlayer.id
    );
    const currentPlayerIndex = playersCopy.findIndex(
      (p) => p.id === currentPlayer.id
    );

    const opponent = { ...playersCopy[opponentIndex] };
    let attacker = { ...playersCopy[currentPlayerIndex] };

    const attackerCharacter = attacker.character;
    const defenderCharacter = opponent.character;
    const attackerElement = attackerCharacter?.element;
    const defenderElement = defenderCharacter?.element;

    let criticalHit = false;
    let totalDamage = 0;
    let elementalBonus = null;

    const totalAccuracy =
      Number(attackerCharacter?.accuracy) +
      Number(
        attackerCharacter?.buffs?.accuracy?.reduce(
          (acc, buff) => acc + buff.amt,
          0
        ) || 0
      );

    if (totalAccuracy >= numberSpun) {
      totalDamage =
        Number(attackerCharacter?.attack) +
        Number(
          attackerCharacter?.buffs?.attack?.reduce(
            (acc, buff) => acc + buff.amt,
            0
          ) || 0
        );

      if (attackerCharacter.accuracy === numberSpun) {
        totalDamage += 1;
        criticalHit = true;
      }

      //   add elemental damage here
      if (attackerElement && defenderElement) {
        console.warn("Elemental Advantage!");
        elementalBonus = getElementalBonus(attackerElement, defenderElement);
        console.warn("+1 damage bonus");
        if (elementalBonus === 1) {
        } else if (elementalBonus === -1) {
          console.warn("-1 damage penalty");
        }
        totalDamage += elementalBonus;
      }

      if (defenderCharacter.stance === "defense") {
        // Damage is applied to defense first
        if (defenderCharacter.defense >= totalDamage) {
          defenderCharacter.defense -= totalDamage;
        } else {
          const remainingDamage = totalDamage - defenderCharacter.defense;
          defenderCharacter.defense = 0;
          defenderCharacter.stance = "attack";
          defenderCharacter.health -= remainingDamage;
        }
      } else {
        // Apply damage directly to health
        defenderCharacter.health -= totalDamage;
      }

      // Check if the defender is dead
      if (defenderCharacter.health <= 0) {
        defenderCharacter.health = 0;
        defenderCharacter.isDead = true;

        // Check if only one player remains alive
        const alivePlayers = playersCopy.filter((p) => !p.character.isDead);
        if (alivePlayers.length === 1) {
          matchData.status = "complete";
          matchData.winner = alivePlayers[0].id;
        }
      }

      console.log(
        criticalHit
          ? `It's a Critical Hit! ${defenderCharacter.name} now has ${defenderCharacter.health} health`
          : `Hit! ${defenderCharacter.name} now has ${defenderCharacter.health} health`
      );
    } else {
      console.log("Missed!");
    }

    // Clear instant accuracy/attack buffs after attack is made
    ["attack", "accuracy"].forEach((stat) => {
      if (Array.isArray(attacker.character.buffs[stat])) {
        attacker.character.buffs[stat] = attacker.character.buffs[stat].filter(
          (buff) => !(buff.duration === 0)
        );
      }
    });

    setSelectedPlayer(null);
    setPlayerMoveType(null);
    setNumberSpun(null);

    if (opponent.character.isDead) {
      console.log(`${opponent.name} is dead!`);

      //   if (matchData?.ranked) {
      await updatePlayerPoints(roomId, attacker.id, {
        damageDealtPoints: matchData?.ranked ? 10 : 0,
        eliminationPoints: matchData?.ranked ? totalDamage * 10 : 0,
        questStats: {
          totalDamageDealt: totalDamage,
          eliminations: 1,
        },
      });
      //   }

      const matchSnap = await getDoc(matchRef);
      const matchDataFresh = matchSnap.data();
      const currentEliminated = matchDataFresh?.eliminated || [];

      const alreadyEliminated = currentEliminated.find(
        (e) => e.id === opponent.id
      );

      if (!alreadyEliminated) {
        const eliminatedEntry = {
          name: opponent.name,
          id: opponent.id,
          character: opponent.character.name,
          eliminatedBy: {
            name: attacker.name,
            id: attacker.id,
            character: attacker.character.name,
          },
        };

        const updatedEliminated = [eliminatedEntry, ...currentEliminated];

        await updateDoc(matchRef, {
          eliminated: updatedEliminated,
        });

        console.log(`Updated matchRef with eliminated player ${opponent.name}`);
      }
    } else {
      //   if (matchData?.ranked) {
      await updatePlayerPoints(roomId, attacker.id, {
        damageDealtPoints: matchData?.ranked ? 10 : 0,
        questStats: {
          totalDamageDealt: totalDamage,
        },
      });
      //   }
    }

    const attackMove = {
      type: totalDamage === 0 ? "attack - missed" : "attack",
      attacker: attacker.name,
      target: opponent.name,
      damage: totalDamage,
      criticalHit,
      //   elementalBonus,
      //   attackerElement,
      //   defenderElement,
      timestamp: Date.now(),
    };

    syncMatchData(attacker.id, playersCopy, attackMove);
    console.log("Setting from here 1");
  };

  const handlePlayCard = async () => {
    if (!selectedCard || !selectedPlayer) return;

    let playersCopy = [...players];
    const selectedPlayerIndex = playersCopy.findIndex(
      (p) => p.id === selectedPlayer.id
    );
    const currentPlayerIndex = playersCopy.findIndex(
      (p) => p.id === currentPlayer.id
    );

    if (selectedPlayerIndex === -1 || currentPlayerIndex === -1) return;

    let targetPlayer = { ...playersCopy[selectedPlayerIndex] };
    let actingPlayer = { ...playersCopy[currentPlayerIndex] };

    // === Parse effect logic
    const { affectedAreas, amount, duration, isAid } =
      parseCardEffect(selectedCard);

    // Target logic: self or other
    const recipient =
      selectedPlayer.id === actingPlayer.id ? actingPlayer : targetPlayer;

    if (selectedCard.specialty) {
      const result = specialtyCardEffect({
        card: selectedCard,
        actingPlayer,
        targetPlayer,
        players: playersCopy,
        hands,
        decks,
        matchRef,
        roomId,
      });

      playersCopy = result.players;
      actingPlayer = playersCopy.find((p) => p.id === currentPlayer.id);

      targetPlayer = playersCopy.find((p) => p.id === selectedPlayer.id);

      let updatedHands = result.hands;
      let updatedDecks = result.decks;

      // Still remove the card from hand
      const cardIndex = updatedHands[actingPlayer.id].findIndex(
        (card) =>
          card.id === selectedCard.id &&
          card.name === selectedCard.name &&
          card.type === selectedCard.type
      );

      if (cardIndex !== -1) {
        updatedHands[actingPlayer.id].splice(cardIndex, 1);
      }

      if (result.forceEndTurn) {
        await updateHandAndDeck(
          roomId,
          actingPlayer.id,
          hands[actingPlayer.id],
          decks[actingPlayer.id]
        );

        const move = {
          type: "playCard",
          player: actingPlayer.name,
          card: selectedCard,
          target: targetPlayer.name,
          timestamp: Date.now(),
        };

        await updatePlayerPoints(roomId, actingPlayer.id, {
          playedCardPoints: matchData?.ranked ? 10 : 0,
          questStats: {
            totalCardsPlayed: 1,
            typesOfCardsPlayed: ["specialty"],
          },
        });

        // === Force turn shift logic
        const latestSnap = await getDoc(matchRef);
        const latestData = latestSnap.data();
        const currentIndex = latestData?.currentPlayerTurn ?? 0;
        const nextIndex = (currentIndex + 1) % latestData.players.length;

        await updateDoc(matchRef, {
          players: playersCopy,
          moves: [...(latestData?.moves?.slice(-25) || []), move],
          message: getToastMessageFromMove(move),
          cpMovesLeft: 0, // Force end of turn
          currentPlayerTurn: nextIndex,
          turnStartTime: Date.now(),
        });

        // Reset UI
        setSelectedCard(null);
        setCardLayout(null);
        setShowPlayerMenu(false);
        setSelectedPlayer(null);
        setPlayerMoveType(null);
        setNumberSpun(null);

        return;
      }

      // Save move and sync
      const move = {
        type: "playCard",
        player: actingPlayer.name,
        card: selectedCard,
        target: targetPlayer.name,
        timestamp: Date.now(),
      };

      await updatePlayerPoints(roomId, actingPlayer.id, {
        playedCardPoints: matchData?.ranked ? 10 : 0,
        questStats: {
          totalCardsPlayed: 1,
          typesOfCardsPlayed: ["specialty"],
        },
      });

      await updateHandAndDeck(
        roomId,
        actingPlayer.id,
        updatedHands[actingPlayer.id],
        updatedDecks[actingPlayer.id]
      );
      syncMatchData(actingPlayer.id, playersCopy, move);

      setSelectedCard(null);
      setCardLayout(null);
      setShowPlayerMenu(false);
      setSelectedPlayer(null);
      setPlayerMoveType(null);
      setNumberSpun(null);

      return;
    }

    affectedAreas.forEach((key) => {
      let area = key.toLowerCase(); // normalize
      if (area === "turn loss") {
        area = "turnLoss";
      }

      const isInstant = duration === 0;
      if (isInstant && area !== "attack" && area !== "accuracy") {
        // Health, Defense, & turnLoss
        const initValue = recipient.character[area]
          ? Number(recipient.character[area])
          : 0;
        recipient.character[area] = initValue + amount;
      } else {
        if (!recipient.character.buffs[area]) {
          recipient.character.buffs[area] = [];
        }

        const buffObj = {
          //   amt: isAid ? amount : -amount, //! Testing...
          amt: amount,
          duration,
          // instant: isInstant,
        };
        recipient.character.buffs[area].push(buffObj);
      }
    });

    let hand = hands?.[actingPlayer.id];
    let deck = decks?.[actingPlayer.id];
    // let hand = hands[actingPlayer.id] || [];
    // let deck = decks[actingPlayer.id] || [];

    // Remove the selected card
    // FIXED card removal logic
    const cardIndex = hand.findIndex(
      (card) =>
        card.id === selectedCard.id &&
        card.name === selectedCard.name &&
        card.type === selectedCard.type
    );

    if (cardIndex !== -1) {
      hand.splice(cardIndex, 1); // removes ONLY ONE instance
    }

    // Update players
    playersCopy[selectedPlayerIndex] = targetPlayer;
    playersCopy[currentPlayerIndex] = actingPlayer;

    const move = {
      type: "playCard",
      player: actingPlayer.name,
      card: selectedCard,
      target: targetPlayer.name,
      effect: { affectedAreas, amount, duration, isAid },
      timestamp: Date.now(),
    };

    // if (matchData?.ranked) {
    await updatePlayerPoints(roomId, actingPlayer.id, {
      playedCardPoints: matchData?.ranked ? 5 : 0,
      questStats: {
        totalCardsPlayed: 1,
        typesOfCardsPlayed: [affectedAreas[0]],
      },
    });
    // }
    // Push updated values to Firestore
    await updateHandAndDeck(roomId, currentPlayer.id, hand, deck);

    syncMatchData(actingPlayer.id, playersCopy, move);

    setSelectedCard(null);
    setCardLayout(null);
    setShowPlayerMenu(false);
    setSelectedPlayer(null);
    setPlayerMoveType(null);
    setNumberSpun(null);
  };

  const handleTradeCard = async () => {
    if (!selectedCard || !selectedPlayer) return;

    let playersCopy = [...players];
    const currentPlayerIndex = playersCopy.findIndex(
      (p) => p.id === currentPlayer.id
    );
    const selectedPlayerIndex = playersCopy.findIndex(
      (p) => p.id === selectedPlayer.id
    );

    if (currentPlayerIndex === -1 || selectedPlayerIndex === -1) return;

    let actingPlayer = { ...playersCopy[currentPlayerIndex] };
    let targetPlayer = { ...playersCopy[selectedPlayerIndex] };

    // ✅ Grab hands from Zustand
    const { hands } = useRoomsStore.getState();
    let handA = hands?.[actingPlayer.id];
    let handB = hands?.[targetPlayer.id];
    // let handA = hands[actingPlayer.id] || [];
    // let handB = hands[targetPlayer.id] || [];

    // 🔄 Find card to return from targetPlayer
    const cardToReturn = [...handB].reduce((lowest, card) => {
      const cardValue = card.max ?? card.amount ?? card.value ?? 0;
      const lowestValue = lowest.max ?? lowest.amount ?? lowest.value ?? 0;
      return cardValue < lowestValue ? card : lowest;
    }, handB[0]);

    // 🔄 Perform the swap
    handA = handA.filter((card) => card.id !== selectedCard.id);
    handB = handB.filter((card) => card.id !== cardToReturn.id);
    handA.push(cardToReturn);
    handB.push(selectedCard);

    // if (matchData?.ranked) {
    await updatePlayerPoints(roomId, actingPlayer.id, {
      tradeCardPoints: matchData?.ranked ? 2 : 0,
      questStats: {
        totalCardsTraded: 1,
      },
    });
    // }

    await Promise.all([
      updateHandAndDeck(roomId, actingPlayer.id, handA), // no deck update needed
      updateHandAndDeck(roomId, targetPlayer.id, handB), // no deck update needed
    ]);

    setCurrentPlayer(actingPlayer);
    setSelectedCard(null);
    setCardLayout(null);
    setShowPlayerMenu(false);

    const move = {
      type: "tradeCard",
      player: actingPlayer.name,
      card: selectedCard.name,
      target: targetPlayer.name,
      timestamp: Date.now(),
    };

    syncMatchData(actingPlayer.id, playersCopy, move);
  };

  const handleDiscardCard = async () => {
    if (!selectedCard || !currentPlayer) return;

    const { hands, decks } = useRoomsStore.getState();
    // let hand = hands?[currentPlayer.id] || [];
    // let deck = decks?[currentPlayer.id] || [];
    let hand = hands?.[currentPlayer.id];
    let deck = decks?.[currentPlayer.id];

    // ❌ Remove selected card
    hand = hand.filter((card) => card.id !== selectedCard.id);

    // ✅ Draw from deck if needed
    // if (hand.length < 5 && deck.length > 0) {
    //   hand.push(deck.shift());
    // }

    // if (matchData?.ranked) {
    await updatePlayerPoints(roomId, userData.id, {
      discardCardPoints: matchData?.ranked ? 5 : 0,
      questStats: {
        totalCardsDiscarded: 1,
      },
    });
    // }
    // ✅ Save updates
    await updateHandAndDeck(roomId, currentPlayer.id, hand, deck);

    setSelectedCard(null);
    setCardLayout(null);
    setShowPlayerMenu(false);

    const move = {
      type: "discardCard",
      player: currentPlayer.name,
      timestamp: Date.now(),
    };

    syncMatchData(currentPlayer.id, players, move);

    console.log(`Card "${selectedCard.name}" discarded from hand.`);
  };

  const handleCardAction = (action) => {
    if (action === "playCard") {
      console.log("Playing Card");
      setCardLayout(null);
      handlePlayCard();
    } else if (action === "tradeCard") {
      console.log("Trading Card");
      setCardLayout(null);
      handleTradeCard();
    } else if (action === "discardCard") {
      console.log("Discarding Card");
      handleDiscardCard();
    }
    setShowPlayerMenu(false);
  };

  const updatePlayerStance = () => {
    const playersCopy = [...players];
    const currentPlayerIndex = playersCopy.findIndex(
      (p) => p.id === userData.id
    );
    const currentPlayer = playersCopy[currentPlayerIndex];
    let currentCharacter = { ...currentPlayer }.character;
    const currentStance = currentCharacter.stance;
    //
    const newStance = currentStance === "defense" ? "attack" : "defense";
    currentCharacter.stance = newStance;
    playersCopy[currentPlayerIndex] = currentPlayer;
    // setPlayers(playersCopy);
    setPlayerMoveType(null);
    const stanceMove = {
      type: "stanceChange",
      player: currentPlayer.name,
      newStance,
      timestamp: Date.now(),
    };

    syncMatchData(currentPlayer.id, playersCopy, stanceMove);
  };

  const resetToast = async () => {
    try {
      await updateDoc(matchRef, {
        message: "", // always force clear
      });
      //   setToastMessage("");
      //   setMessage("");
    } catch (err) {
      console.error("Error clearing toast:", err);
    }
  };

  const { infinityCards, characterCards } = useCardStore();

  // ! LOGGING THE MOUNT ============================================================
  useEffect(() => {
    console.log("🧪 useTurnEngine init:", {
      matchData,
      userData,
      syncMatchData,
    });
    setTimeout(() => {
      console.log("timing out...");
      setGameScreenLoading(false);
    }, 3500); // fail safe small delay, but lets TransitionOverlay take over
    console.log("🎮 GameBoardScreen Mounted!");
    return () => console.log("🛑 GameBoardScreen Unmounted!");
  }, []);

  // ! RESPONSIBLE FOR STARTING THE MATCH ===========================================
  useEffect(() => {
    const startMatch = async () => {
      await updateDoc(matchRef, {
        message: "Match Start!",
        currentPlayerTurn: 0,
        moves: [
          {
            type: "matchStart",
            timestamp: Date.now(),
          },
        ],
      });
    };

    if (matchData?.message === "" && matchData?.currentPlayerTurn === -1) {
      startMatch();
    }

    if (matchData?.message?.length > 0 && (!message || message === "")) {
      //   setToastMessage(matchData.message);
      setMessage(matchData.message);
      if (
        matchData.message.includes(
          "attacked" || matchData.message.includes("they lost")
        )
      ) {
        setError(true); //red
      } else if (
        matchData.message.includes("played") ||
        matchData.message.includes("+")
      ) {
        setSuccess(true); //green
      } else {
        setInfo(true); //blue
      }
    }
    // console.log("NEW TOAST MESSAGE =>", matchData?.message);
    // console.log("TOAST MESSAGE LOCAL => ", message);
  }, [matchData?.message, message]);

  // ! RESPONSIBLE FOR SETTING AND TRIGGERING ANIMATIONS ============================
  //TODO: Take a look a SlapCard Animation triggers
  useEffect(() => {
    if (!matchData?.moves?.length) return;

    const lastMove = matchData.moves[matchData.moves.length - 1];
    const { type, attacker, player, target, timestamp, card } = lastMove;

    if (timestamp === lastAnimationTimestampRef.current) return;

    // 🔥 ATTACK → PlayerPlatform animation directly
    if (type === "attack") {
      setAttackAnimation({
        type: "attack",
        attacker,
        target,
        timestamp,
      });

      lastAnimationTimestampRef.current = timestamp;
    }

    // 🎴 PLAY CARD → Triggers Slap animation
    else if (type === "playCard" && card) {
      setActiveCardPlayAnimation({
        type: "playCard",
        player,
        target,
        card,
        timestamp,
      });

      lastAnimationTimestampRef.current = timestamp;
    } else if (type === "tradeCard") {
      setShowTradeAnimation(true);
      lastAnimationTimestampRef.current = timestamp;
    }
  }, [matchData?.moves]);

  useEffect(() => {
    if (!activeAnimation || activeAnimation.type !== "playCard") return;
    console.log("🎴 Triggering SlapCardAnimation...");

    const actor = players.find(
      (p) =>
        p.name === activeAnimation.player ||
        p.username === activeAnimation.player
    );
    const lastMove = matchData?.moves?.[matchData.moves.length - 1];

    if (actor && lastMove?.card) {
      setSlapCardData(lastMove.card); // boom — now you get full card details
      setShowSlapAnimation(true);
    }
  }, [activeAnimation]);

  // ! RESPONSIBLE FOR SETTING PLAYERS LOCALLY FOR RENDERING ========================
  useEffect(() => {
    if (matchData?.players?.length) {
      //   console.log("🧠 MatchData Players:", matchData.players); //
      if (matchData.players[matchData.currentPlayerTurn]?.character?.isDead) {
        // setToastMessage("Player is dead!");
        setMessage("Player is dead! Skipping turn...");
        setInfo(true);
      }

      //   //! THIS IS WHERE I WAS TRYING TO TRIGGER THE THE TURN LOSS LOGIC
      //   if (
      //     !turnLossRef.current &&
      //     matchData.players[matchData.currentPlayerTurn]?.character?.turnLoss > 0
      //   ) {
      //     console.warn("THIS IS BEING TRIGGERED!");
      //     turnLossRef.current = true;

      //     let theCurrentPlayer = matchData.players[matchData.currentPlayerTurn];
      //     let thePlayers = matchData.players;
      //     const currentTurnLossValue = theCurrentPlayer.character.turnLoss;

      //     getAvailableHuman(thePlayers, roomId).then(async (human) => {
      //       if (human?.id === userData.id) {
      //         let valueToSubtract =
      //           currentTurnLossValue > 2 ? 2 : currentTurnLossValue;
      //         let balance =
      //           currentTurnLossValue - valueToSubtract < 0
      //             ? 0
      //             : currentTurnLossValue - valueToSubtract;

      //         theCurrentPlayer.character.turnLoss = balance;
      //         thePlayers = [...thePlayers].map((player) => {
      //           if (player.id === theCurrentPlayer.id) {
      //             return {
      //               ...player,
      //               character: {
      //                 ...player.character,
      //                 turnLoss: balance,
      //               },
      //             };
      //           }
      //           return player;
      //         });

      //         let newMove = {
      //           type: "turnLoss",
      //           player: theCurrentPlayer.name,
      //           amount: valueToSubtract,
      //           timestamp: Date.now(),
      //         };

      //         let newMovesArray = [...(matchData.moves || []), newMove];
      //         const nextIndex = getNextLivingPlayerIndex(
      //           matchData.players,
      //           matchData.currentPlayerTurn
      //         );

      //         console.warn("🔻 the next index", nextIndex);

      //         setTimeout(async () => {
      //           await updateDoc(matchRef, {
      //             cpMovesLeft:
      //               Math.max(matchData.cpMovesLeft - valueToSubtract, 0) === 0
      //                 ? 2
      //                 : matchData.cpMovesLeft - valueToSubtract,
      //             currentPlayerTurn: nextIndex,
      //             players: thePlayers,
      //             moves: newMovesArray,
      //             message: `${
      //               theCurrentPlayer.name
      //             } was affect by a turn loss and lost ${valueToSubtract} move${
      //               valueToSubtract > 1 ? "s" : ""
      //             }!`,
      //           });
      //         }, 2000);
      //         // setMessage(`${theCurrentPlayer.name} was affect by a turn loss and lost ${valueToSubtract} move${valueToSubtract > 1 ? "s" : ""}!`);
      //         // setInfo(true);
      //         console.warn("🔻 cpMovesLeft decremented due to timeout");
      //       }
      //     });
      //   } else if (
      //     matchData.players[matchData.currentPlayerTurn]?.character?.turnLoss ===
      //     0
      //   ) {
      //     turnLossRef.current = false;
      //   }

      setPlayers(matchData.players);
      const current = matchData.players.find((p) => p.id === userData.id);
      let playerturn = matchData.players[matchData.currentPlayerTurn];
      //   console.log("🧠 playerturn:", playerturn);
      setIsMyTurn(playerturn?.id === userData.id);
      console.log("🧠 Is my turn:", playerturn?.id === userData.id);
      setCurrentPlayer(current);
    }
  }, [matchData?.currentPlayerTurn, matchData?.players]);

  // ! RESPONSIBLE FOR WATCHING LIVE DB UPDATES (SYNC) ===============================
  useEffect(() => {
    console.log("2");
    subscribeToMatch(roomId, matchId);
    return () => useRoomsStore.getState().unsubscribeFromMatch();
  }, [roomId, matchId]);

  useEffect(() => {
    if (roomId && userData?.id) {
      console.log("🧠 Subscribing to chat thread");
      subscribeToChatThread(roomId, userData.id);
    }
    return () => {
      useRoomsStore.getState().unsubscribeFromChatThread();
    };
  }, [roomId, userData?.id]);

  useEffect(() => {
    console.log("🧠 Hands:", hands);
  }, [hands, decks, currentPlayer]);

  // ! RESPONSIBLE FOR TRACKING PLAYER TURN IN CLIENT =================================
  useEffect(() => {
    console.log("3");
    console.log("Players move type:", playerMoveType);
    console.log("Is my turn:", isMyTurn);
  }, [playerMoveType, isMyTurn]);

  // ! RESPONSIBLE FOR HANDLING ATTACK ===============================================
  useEffect(() => {
    if (numberSpun) {
      if (playerMoveType === "attack") {
        handleAttack();
      }
    }
  }, [numberSpun]);

  // ! TIMER - RESPONSIBLE KEEPING TRACK OF TIME OF LAST MOVE =========================
  useEffect(() => {
    if (
      !lastMoveTimestamp ||
      !players?.length ||
      !userData ||
      !matchData ||
      matchData.status === "finished" ||
      matchData.winner
    ) {
      return;
    }

    if (timerRef.current) clearInterval(timerRef.current);

    const updateElapsed = async () => {
      if (
        matchData.status === "finished" ||
        matchData.winner ||
        matchData?.eliminated.find((p) => p.id === userData.id)
      )
        return;

      const now = Date.now();
      const diff = Math.floor((now - lastMoveTimestamp) / 1000);
      setElapsedSeconds(diff);

      const currentPlayer = players[matchData.currentPlayerTurn];

      // ⏳ seconds Turn Timeout Logic — only if not already triggered
      let timer = matchData?.turnTime ?? 30;

      if (
        diff >= timer &&
        matchData.cpMovesLeft > 0 &&
        !timeoutTriggeredRef.current
      ) {
        getAvailableHuman(players, roomId).then(async (human) => {
          if (human?.id === userData.id) {
            timeoutTriggeredRef.current = true;

            let newMove = {
              type: "playerSkipped",
              player: currentPlayer.name,
              timestamp: Date.now(),
            };

            let newMovesArray = [...matchData.moves, newMove];

            if (Math.max(matchData.cpMovesLeft, 0) > 1) {
              await updateDoc(matchRef, {
                cpMovesLeft: Math.max(matchData.cpMovesLeft - 1, 0),
                turnStartTime: now,
                moves: newMovesArray,
              });
            } else {
              await updateDoc(matchRef, {
                cpMovesLeft: 2,
                currentPlayerTurn: getNextLivingPlayerIndex(
                  matchData.players,
                  matchData.currentPlayerTurn
                ),
                turnStartTime: now,
                moves: newMovesArray,
              });
            }

            setTimeout(() => {
              timeoutTriggeredRef.current = false;
            }, 3000);

            console.log("🔻 cpMovesLeft decremented due to timeout");
          }
        });
      }
    };

    updateElapsed(); // immediate run
    timerRef.current = setInterval(updateElapsed, 1000);

    return () => {
      clearInterval(timerRef.current);
    };
  }, [
    lastMoveTimestamp,
    matchData?.currentPlayerTurn,
    matchData?.winner,
    matchData?.status,
  ]);

  useEffect(() => {
    timeoutTriggeredRef.current = false;
  }, [lastMoveTimestamp]);

  // ! RESPONSIBLE FOR FINDING 1 LIVE PLAYER IN CLIENT (FOR BOTS) =====================
  useEffect(() => {
    console.log("5");
    if (userData?.id && roomId) {
      updatePlayerPresence(userData, {
        roomId,
        online: true,
        inGameRoom: true,
      });
    }
  }, [userData?.id, roomId]);

  // ! RESPONSIBLE FOR TURN ALL LOGIC ===============================================
  useEffect(() => {
    const processTurn = async () => {
      if (
        matchData?.currentPlayerTurn === undefined ||
        matchData?.cpMovesLeft === undefined ||
        !players?.length ||
        !userData?.id
      ) {
        return;
      }

      hasStartedBotTurnRef.current = false;

      console.log(
        "Processing the turn",
        matchData?.currentPlayerTurn,
        matchData?.cpMovesLeft,
        players?.length,
        userData?.id
      );

      const turnKey = `${matchData.currentPlayerTurn}_${matchData.cpMovesLeft}`;
      if (hasProcessedTurnRef.current === turnKey) return;
      hasProcessedTurnRef.current = turnKey;

      const currentPlayer = players[matchData.currentPlayerTurn];

      if (!currentPlayer) return;
      let currentCharacter = { ...currentPlayer?.character };
      let updatedPlayers = [...players];
      if (currentCharacter.isDead) {
        console.warn("💀 Player is dead — skipping turn:", currentPlayer.name);
        nextTurn(); // skip to next player
        return;
      }

      const isCurrentClientTurn = currentPlayer?.id === userData.id;
      const isBot = currentPlayer?.isBot;

      // STEP 1: Apply debuffs & turn loss at the start of a new player's turn
      const updateMatchDataAfterBuffDebuff = async (
        updatedPlayers,
        hasTurnLoss,
        movesLeft,
        message = ""
      ) => {
        if (hasTurnLoss) {
          const matchRef = doc(db, "rooms", roomId, "matchData", matchId);
          await updateDoc(matchRef, {
            cpMovesLeft: movesLeft,
            players: updatedPlayers,
            message: message,
          });
        } else {
          const matchRef = doc(db, "rooms", roomId, "matchData", matchId);
          await updateDoc(matchRef, {
            players: updatedPlayers,
            message: message,
          });
        }
      };

      if (matchData?.cpMovesLeft >= 2) {
        console.log("🔁 STEP 1: Applying turn buff for", currentCharacter);
        const { character: updatedCharacter, appliedBuffs } =
          applyTurnBuffs(currentCharacter);
        currentCharacter = updatedCharacter;

        // 🪦 Check if player died after buffs were applied
        if (currentCharacter.isDead) {
          console.warn(
            "💀 Player died after buff application:",
            currentPlayer.name
          );

          const matchRef = doc(db, "rooms", roomId, "matchData", matchId);
          const matchSnap = await getDoc(matchRef);
          const matchDataFresh = matchSnap.data();
          const currentEliminated = matchDataFresh?.eliminated || [];

          const alreadyEliminated = currentEliminated.find(
            (e) => e.id === currentPlayer.id
          );

          if (!alreadyEliminated) {
            const eliminatedEntry = {
              name: currentPlayer.name,
              id: currentPlayer.id,
              character: currentPlayer.character.name,
              eliminatedBy: {
                name: currentPlayer.name, // You can change this to whoever last attacked, if tracked
                id: currentPlayer.id,
                character: currentPlayer.character.name,
              },
            };

            const updatedEliminated = [eliminatedEntry, ...currentEliminated];

            await updateDoc(matchRef, {
              eliminated: updatedEliminated,
            });
          }

          // Skip to next turn since player is dead
          nextTurn();
          return;
        }

        const hasTurnLoss = currentCharacter?.turnLoss > 0; // will be positive numbers if not zero
        let movesLeft = 2;

        const lostMoves = matchData.cpMovesLeft - movesLeft;
        const updatedMessage = buildBuffMessage(
          currentPlayer.name,
          appliedBuffs,
          hasTurnLoss,
          lostMoves
        );
        if (hasTurnLoss) {
          console.warn("🔁 STEP 1: Applying turn loss for", currentPlayer.name);
          if (currentCharacter.turnLoss === 1) {
            currentCharacter.turnLoss = 0;
            movesLeft = 1;
          } else {
            currentCharacter.turnLoss -= 2;
            movesLeft = 0;
          }
        }

        //update match data
        updatedPlayers = updatedPlayers.map((p) =>
          p.id === currentPlayer.id ? { ...p, character: currentCharacter } : p
        );
        await updateMatchDataAfterBuffDebuff(
          updatedPlayers,
          hasTurnLoss,
          movesLeft,
          updatedMessage
        );

        // const freshSnap = await getDoc(matchRef); //! testing...
        // const freshData = freshSnap.data();

        // setPlayers(freshData.players);
      }

      // STEP 2: Check if current player is bot or human
      // STEP 3: Move to next player if no actions remain
      console.log("⏭️ STEP 3: No moves left. Advancing to next player.");
      if (matchData?.cpMovesLeft === 0) {
        // Your logic: find next living player and restart the turn cycle
        nextTurn();
      } else {
        if (isBot) {
          console.warn(
            "🤖 STEP 2a: Current player is a bot. Get available human for fallback logic."
          );
          const maybeRunBot = async () => {
            const playerIndex = matchData?.currentPlayerTurn;
            if (typeof playerIndex !== "number" || !players[playerIndex]) {
              console.warn(
                "🛑 Invalid player index or player not loaded yet:",
                {
                  playerIndex,
                  players,
                }
              );
              return;
            }

            const current = players[playerIndex];
            console.log("🚦 maybeRunBot invoked for:", current?.name);

            const human = await getAvailableHuman(players, roomId);
            if (!human || human.id !== userData.id) return;

            if (hasStartedBotTurnRef.current) return;

            hasStartedBotTurnRef.current = true;
            // botTurnStartRef.current = Date.now();

            try {
              console.log("ATTEMPTING 🤖 BOT TURN...");
              await simulateBotTurn(
                current,
                players,
                matchData,
                roomId,
                syncMatchData,
                true,
                // nextTurn,
                hands,
                decks,
                hasStartedBotTurnRef
              );
            } catch (err) {
              console.error("🤖 simulateBotTurn error:", err);
            }
            //  finally {
            //   setTimeout(() => {
            //     hasStartedBotTurnRef.current = false;
            //     console.warn("✅ Resetting bot ref");
            //   }, 15000);
            // }
          };

          await maybeRunBot();

          return;
        } else if (isCurrentClientTurn) {
          console.warn(
            "🧠 STEP 2b: Current player is a human and it's my turn. Awaiting user move."
          );
          // Nothing need to happen here... just waiting for user input
        }
      }
    };

    processTurn();
  }, [matchData?.currentPlayerTurn, matchData?.cpMovesLeft, players, userData]);

  // ! RESPONSIBLE FOR CHECKING FOR THE WINNER OF A MATCH ===========================
  // useEffect(() => {
  //   if (
  //     !userData?.id ||
  //     !roomId ||
  //     !matchData?.players ||
  //     !matchData?.eliminated ||
  //     !matchRef ||
  //     !matchData
  //   ) {
  //     return;
  //   }

  //   if (hasHandledEndOfMatch.current) return; // ✅ prevent double run

  //   const alivePlayers = matchData?.players?.filter(
  //     (p) => !matchData.eliminated.some((e) => e.id === p.id)
  //   );

  //   console.log("🧠 Alive players:", alivePlayers.length);

  //   if (
  //     alivePlayers.length === 1 &&
  //     matchData.status !== "finished" &&
  //     !matchData.winner
  //   ) {
  //     getAvailableHuman(matchData.players, roomId).then((human) => {
  //       if (human?.id === userData.id) {
  //         const winner = alivePlayers[0];
  //         const winnerInfo = {
  //           name: winner.name,
  //           id: winner.id,
  //           characterName: winner.character.name,
  //         };

  //         console.log(
  //           "🎉 I’m the surviving client. Declaring winner:",
  //           winnerInfo
  //         );

  //         hasHandledEndOfMatch.current = true;

  //         updateDoc(matchRef, {
  //           status: "finished",
  //           winner: winnerInfo,
  //         }).then(async () => {
  //           console.log("🏁 Match status set to finished, winner saved.");

  //           // 1. Snapshot all players' XP *before* awarding points
  //           const snapshotXP = await gatherAllPlayersXP();

  //           // 2. Fetch playerPoints (awarded points for each player)
  //           const playerPointsRef = doc(
  //             db,
  //             "rooms",
  //             roomId,
  //             "matchData",
  //             "playerPoints"
  //           );
  //           const playerPointsSnap = await getDoc(playerPointsRef);
  //           const playerPointsData = playerPointsSnap.data() || {};
  //           setPlayerPoints(playerPointsData);

  //           const eliminated = matchData.eliminated;
  //           const orderedPlacements = [...eliminated].reverse(); // 3rd out → 3rd place

  //           const PRESEASON_MODE = true;
  //           const PRESEASON_XP_CAP = 4439;

  //           const placementMap = {
  //             0: 50, // 3rd place
  //             1: 75, // 2nd place
  //             2: 100, // 1st place (winner)
  //           };

  //           for (const player of matchData.players) {
  //             if (player.isBot) continue;

  //             const userId = player.id;
  //             const userRef = doc(db, "users", userId);

  //             const oldXP = Number(snapshotXP[userId]?.xp) ?? 0;
  //             const playerPointData = playerPointsData[userId] || {};
  //             const { questStats, ...pointValuesOnly } = playerPointData;

  //             let totalAwardedPoints = Object.values(pointValuesOnly).reduce(
  //               (sum, val) => sum + val,
  //               0
  //             );

  //             // Placement bonus
  //             const eliminatedIndex = orderedPlacements.findIndex(
  //               (e) => e.id === userId
  //             );
  //             if (eliminatedIndex !== -1 && matchData?.ranked) {
  //               const bonus = placementMap[eliminatedIndex] ?? 25;
  //               totalAwardedPoints += bonus;
  //             }

  //             let newRecord = player?.record || {
  //               wins: 0,
  //               losses: 0,
  //               ties: 0,
  //               dnfs: 0,
  //             };

  //             // Winner bonus
  //             if (userId === winner.id && matchData?.ranked) {
  //               totalAwardedPoints += placementMap[2];
  //               newRecord.wins += 1;
  //             } else {
  //               newRecord.losses += 1;
  //             }

  //             if (matchData?.ranked) {
  //               let newXP = oldXP + totalAwardedPoints;

  //               if (PRESEASON_MODE && oldXP < PRESEASON_XP_CAP) {
  //                 newXP = Math.min(newXP, PRESEASON_XP_CAP);
  //               }

  //               if (!PRESEASON_MODE || oldXP < PRESEASON_XP_CAP) {
  //                 console.log(
  //                   `✨ Updating XP for ${player.name}: Old XP = ${oldXP}, New XP = ${newXP}`
  //                 );

  //                 await updateDoc(userRef, { xp: newXP, record: newRecord });
  //                 await checkAndUpdateLevel(
  //                   player,
  //                   newXP,
  //                   infinityCards,
  //                   characterCards
  //                 );
  //               } else {
  //                 console.log(
  //                   `⛔ Preseason cap hit for ${player.name} — skipping XP update.`
  //                 );
  //               }
  //             }

  //             // Character cleanup if they lost
  //             const didLose = userId !== winner.id;
  //             if (didLose && matchData?.ranked) {
  //               const charCardId = player.character?.id;
  //               if (charCardId) {
  //                 const charRef = doc(
  //                   db,
  //                   "users",
  //                   userId,
  //                   "Character cards",
  //                   charCardId
  //                 );
  //                 const charSnap = await getDoc(charRef);
  //                 const originalCard = charSnap.data();

  //                 if (originalCard) {
  //                   let cleanCharacter = { ...originalCard };
  //                   delete cleanCharacter.boosts;

  //                   console.log(`🧼 Restoring character for ${player.name}`);
  //                   await setDoc(charRef, cleanCharacter);
  //                 }
  //               }
  //             }

  //             const enrichedQuestStats = {
  //               ...(questStats || {}),
  //               characterElement: player?.character?.element || "unknown",
  //               characterName: player?.character?.name || "unknown",
  //               characterRarity: player?.character?.rarity || "unknown",
  //               matchWon: userId === winner.id,
  //             };

  //             await addNewLogToQuestLogs(userId, enrichedQuestStats);
  //           }

  //           // ✅ Now ready
  //           setShowResultsModal(true);
  //         });
  //       }
  //     });
  //   }

  //   // Just display the modal if match already marked finished
  //   if (
  //     matchData.status === "finished" &&
  //     matchData.winner &&
  //     !showResultsModal
  //   ) {
  //     console.log("🏆 Match is over, showing modal.");
  //     setShowResultsModal(true);
  //   }
  // }, [
  //   userData?.id,
  //   roomId,
  //   matchData?.players,
  //   matchData?.eliminated,
  //   matchData?.status,
  //   matchData?.winner,
  // ]);

  useEffect(() => {
    if (
      !userData?.id ||
      !roomId ||
      !matchData?.players ||
      !matchData?.eliminated ||
      !matchRef ||
      !matchData
    ) {
      return;
    }

    if (hasHandledEndOfMatch.current) return; // ✅ prevent double run

    const alivePlayers = matchData?.players?.filter(
      (p) => !matchData.eliminated.some((e) => e.id === p.id)
    );

    console.log("🧠 Alive players:", alivePlayers.length);

    if (matchData.pve && matchData.status !== "finished" && !matchData.winner) {
      const aliveBots = matchData.players.filter(
        (p) => p.isBot && !matchData.eliminated.some((e) => e.id === p.id)
      );

      if (aliveBots.length === 0 && matchData.pve) {
        getAvailableHuman(matchData.players, roomId).then((human) => {
          if (human?.id === userData.id) {
            const humanWinners = matchData.players.filter(
              (p) =>
                //May cause elimated players to not be winners
                !p.isBot && !matchData.eliminated.some((e) => e.id === p.id)
            );

            const winner = humanWinners[0]; // arbitrarily pick first human for record

            const winnerInfo = {
              name: winner.name,
              id: winner.id,
              characterName: winner.character.name,
            };

            console.log(
              "🎉 All bots eliminated. Declaring human win:",
              winnerInfo
            );

            hasHandledEndOfMatch.current = true;

            updateDoc(matchRef, {
              status: "finished",
              winner: winnerInfo,
            }).then(async () => {
              console.log("🏁 Match status set to finished, PvE winner saved.");

              // ⬇️ Continue your XP + rewards logic as usual...

              // 1. Snapshot all players' XP *before* awarding points
              const snapshotXP = await gatherAllPlayersXP();

              // 2. Fetch playerPoints (awarded points for each player)
              const playerPointsRef = doc(
                db,
                "rooms",
                roomId,
                "matchData",
                "playerPoints"
              );

              const playerPointsSnap = await getDoc(playerPointsRef);
              const playerPointsData = playerPointsSnap.data() || {};
              setPlayerPoints(playerPointsData);

              const eliminated = matchData.eliminated;
              const orderedPlacements = [...eliminated].reverse(); // 3rd out → 3rd place

              const PRESEASON_MODE = true;
              const PRESEASON_XP_CAP = 4439;

              const placementMap = {
                0: 50, // 3rd place
                1: 75, // 2nd place
                2: 100, // 1st place (winner)
              };

              for (const player of matchData.players) {
                if (player.isBot) continue;

                const userId = player.id;
                const userRef = doc(db, "users", userId);

                const oldXP = Number(snapshotXP[userId]?.xp) ?? 0;
                const playerPointData = playerPointsData[userId] || {};
                const { questStats, ...pointValuesOnly } = playerPointData;

                let totalAwardedPoints = Object.values(pointValuesOnly).reduce(
                  (sum, val) => sum + val,
                  0
                );

                // Placement bonus
                const eliminatedIndex = orderedPlacements.findIndex(
                  (e) => e.id === userId
                );
                if (eliminatedIndex !== -1 && matchData?.ranked) {
                  const bonus = placementMap[eliminatedIndex] ?? 25;
                  totalAwardedPoints += bonus;
                }

                let newRecord = player?.record || {
                  wins: 0,
                  losses: 0,
                  ties: 0,
                  dnfs: 0,
                };

                // Winner bonus

                if (matchData?.ranked) {
                  if (
                    matchData.players.find(
                      (p) => p.id === winnerInfo.id && !p.isBot
                    )
                  ) {
                    totalAwardedPoints += placementMap[2];
                    newRecord.wins += 1;
                  } else {
                    newRecord.losses += 1;
                  }
                }

                //XP Bonus
                if (matchData?.ranked) {
                  let newXP = oldXP + totalAwardedPoints;

                  if (PRESEASON_MODE && oldXP < PRESEASON_XP_CAP) {
                    newXP = Math.min(newXP, PRESEASON_XP_CAP);
                  }

                  if (!PRESEASON_MODE || oldXP < PRESEASON_XP_CAP) {
                    console.log(
                      `✨ Updating XP for ${player.name}: Old XP = ${oldXP}, New XP = ${newXP}`
                    );

                    await updateDoc(userRef, { xp: newXP, record: newRecord });

                    if (!levelUpHandledRef.current[player.id]) {
                      levelUpHandledRef.current[player.id] = true;

                      await checkAndUpdateLevel(
                        { ...player, xp: oldXP },
                        newXP,
                        infinityCards,
                        characterCards
                      );
                    }
                  } else {
                    console.log(
                      `⛔ Preseason cap hit for ${player.name} — skipping XP update.`
                    );
                  }
                }

                // Character cleanup if they lost
                const didLose = matchData.players.find(
                  (p) => p.id === winnerInfo.id && p.isBot
                );
                if (didLose && matchData?.ranked) {
                  const charCardId = player.character?.id;
                  if (charCardId) {
                    const charRef = doc(
                      db,
                      "users",
                      userId,
                      "Character cards",
                      charCardId
                    );
                    const charSnap = await getDoc(charRef);
                    const originalCard = charSnap.data();

                    if (originalCard) {
                      let cleanCharacter = { ...originalCard };
                      delete cleanCharacter.boosts;

                      console.log(`🧼 Restoring character for ${player.name}`);
                      await setDoc(charRef, cleanCharacter);
                    }
                  }
                }

                const enrichedQuestStats = {
                  ...(questStats || {}),
                  characterElement: player?.character?.element || "unknown",
                  characterName: player?.character?.name || "unknown",
                  characterRarity: player?.character?.rarity || "unknown",
                  matchWon: userId === winner.id,
                };

                await addNewLogToQuestLogs(userId, enrichedQuestStats);
              }

              // ✅ Now ready
              setShowResultsModal(true);
            });
          }
        });
        return; // ✅ Exit early so standard win condition doesn't run too
      }
    }

    if (
      alivePlayers.length === 1 &&
      matchData.status !== "finished" &&
      !matchData.winner
    ) {
      getAvailableHuman(matchData.players, roomId).then((human) => {
        if (human?.id === userData.id) {
          const winner = alivePlayers[0];
          const winnerInfo = {
            name: winner.name,
            id: winner.id,
            characterName: winner.character.name,
          };

          console.log(
            "🎉 I’m the surviving client. Declaring winner:",
            winnerInfo
          );

          hasHandledEndOfMatch.current = true;

          updateDoc(matchRef, {
            status: "finished",
            winner: winnerInfo,
          }).then(async () => {
            console.log("🏁 Match status set to finished, winner saved.");

            // 1. Snapshot all players' XP *before* awarding points
            const snapshotXP = await gatherAllPlayersXP();

            // 2. Fetch playerPoints (awarded points for each player)
            const playerPointsRef = doc(
              db,
              "rooms",
              roomId,
              "matchData",
              "playerPoints"
            );
            const playerPointsSnap = await getDoc(playerPointsRef);
            const playerPointsData = playerPointsSnap.data() || {};
            setPlayerPoints(playerPointsData);

            const eliminated = matchData.eliminated;
            const orderedPlacements = [...eliminated].reverse(); // 3rd out → 3rd place

            const PRESEASON_MODE = true;
            const PRESEASON_XP_CAP = 4439;

            const placementMap = {
              0: 50, // 3rd place
              1: 75, // 2nd place
              2: 100, // 1st place (winner)
            };

            for (const player of matchData.players) {
              if (player.isBot) continue;

              const userId = player.id;
              const userRef = doc(db, "users", userId);

              const oldXP = Number(snapshotXP[userId]?.xp) ?? 0;
              const playerPointData = playerPointsData[userId] || {};
              const { questStats, ...pointValuesOnly } = playerPointData;

              let totalAwardedPoints = Object.values(pointValuesOnly).reduce(
                (sum, val) => sum + val,
                0
              );

              // Placement bonus
              const eliminatedIndex = orderedPlacements.findIndex(
                (e) => e.id === userId
              );
              if (eliminatedIndex !== -1 && matchData?.ranked) {
                const bonus = placementMap[eliminatedIndex] ?? 25;
                totalAwardedPoints += bonus;
              }

              let newRecord = player?.record || {
                wins: 0,
                losses: 0,
                ties: 0,
                dnfs: 0,
              };

              // Winner bonus
              if (userId === winner.id && matchData?.ranked) {
                totalAwardedPoints += placementMap[2];
                newRecord.wins += 1;
              } else {
                newRecord.losses += 1;
              }

              if (matchData?.ranked) {
                let newXP = oldXP + totalAwardedPoints;

                if (PRESEASON_MODE && oldXP < PRESEASON_XP_CAP) {
                  newXP = Math.min(newXP, PRESEASON_XP_CAP);
                }

                if (!PRESEASON_MODE || oldXP < PRESEASON_XP_CAP) {
                  console.log(
                    `✨ Updating XP for ${player.name}: Old XP = ${oldXP}, New XP = ${newXP}`
                  );

                  await updateDoc(userRef, { xp: newXP, record: newRecord });

                  if (!levelUpHandledRef.current[player.id]) {
                    levelUpHandledRef.current[player.id] = true;

                    await checkAndUpdateLevel(
                      { ...player, xp: oldXP },
                      newXP,
                      infinityCards,
                      characterCards
                    );
                  }
                } else {
                  console.log(
                    `⛔ Preseason cap hit for ${player.name} — skipping XP update.`
                  );
                }
              }

              // Character cleanup if they lost
              const didLose = userId !== winner.id;
              if (didLose && matchData?.ranked) {
                const charCardId = player.character?.id;
                if (charCardId) {
                  const charRef = doc(
                    db,
                    "users",
                    userId,
                    "Character cards",
                    charCardId
                  );
                  const charSnap = await getDoc(charRef);
                  const originalCard = charSnap.data();

                  if (originalCard) {
                    let cleanCharacter = { ...originalCard };
                    delete cleanCharacter.boosts;

                    console.log(`🧼 Restoring character for ${player.name}`);
                    await setDoc(charRef, cleanCharacter);
                  }
                }
              }

              const enrichedQuestStats = {
                ...(questStats || {}),
                characterElement: player?.character?.element || "unknown",
                characterName: player?.character?.name || "unknown",
                characterRarity: player?.character?.rarity || "unknown",
                matchWon: userId === winner.id,
              };

              await addNewLogToQuestLogs(userId, enrichedQuestStats);
            }

            // ✅ Now ready
            setShowResultsModal(true);
          });
        }
      });
    }

    // Just display the modal if match already marked finished
    if (
      matchData.status === "finished" &&
      matchData.winner &&
      !showResultsModal
    ) {
      console.log("🏆 Match is over, showing modal.");
      setShowResultsModal(true);
    }
  }, [
    userData?.id,
    roomId,
    matchData?.players,
    matchData?.eliminated,
    matchData?.status,
    matchData?.winner,
  ]);

  const selectCard = (card, layout) => {
    console.log(card);
    setSelectedCard(card);
    setCardLayout(layout);
  };

  const closeSelectedCard = () => {
    setTimeout(() => {
      setSelectedCard(null);
      setCardLayout(null);
    }, 300);
  };

  const handleQuitMatch = async () => {
    setGameScreenLoading(true);
    try {
      const eliminatedEntry = {
        name: currentPlayer.name,
        id: currentPlayer.id,
        character: currentPlayer.character.name,
        eliminatedBy: {
          name: currentPlayer.name,
          id: currentPlayer.id,
          character: currentPlayer.character.name,
        },
      };

      let updatedPlayers = matchData.players.map((player) => {
        if (player.id === userData.id) {
          return {
            ...player,
            character: { ...player.character, isDead: true },
          };
        }
        return player;
      });

      const updatedEliminated = [
        ...(matchData.eliminated || []),
        eliminatedEntry,
      ];

      const userCharacter = currentPlayer?.character;
      const charCardId = userCharacter?.id;

      if (!charCardId) {
        console.warn("❌ Character ID missing — skipping cleanup.");
      } else {
        const charRef = doc(
          db,
          "users",
          userData.id,
          "Character cards",
          charCardId
        );

        // Option 1: Fetch the original card to restore baseline stats
        const charSnap = await getDoc(charRef);
        const originalCard = charSnap.data();

        if (!originalCard) {
          console.warn("❌ Failed to fetch original character card.");
        } else {
          // Rebuild the card with default stats + no boosts
          let cleanCharacter = {
            ...originalCard,
          };

          delete cleanCharacter.boosts;

          console.log(
            "🧼 Restoring lost character to clean state:",
            cleanCharacter
          );

          await setDoc(charRef, cleanCharacter);
        }
      }

      // ✅ Check if any other human players are alive
      const otherAliveHumans = matchData.players.filter(
        (p) => p.id !== userData.id && !p.character?.isDead && !p.isBot
      );

      let matchUpdate = {
        players: updatedPlayers,
        eliminated: arrayUnion(eliminatedEntry),
      };

      if (otherAliveHumans.length === 0) {
        matchUpdate.status = "finished";
        matchUpdate.winner = null; // or assign last bot as winner if needed
      }

      await updateDoc(matchRef, matchUpdate);

      await handleEndMatch(true);
    } catch (error) {
      console.error("Error quitting match:", error);
    }
  };

  const handleEndMatch = async (isAlreadyLoading = false) => {
    if (!isAlreadyLoading) {
      setGameScreenLoading(true);
    }
    const userRef = doc(db, "users", userData.id);
    const dbRef = getDatabase();
    const presenceRef = ref(dbRef, `presence/${userData.id}`);

    await updateDoc(userRef, {
      currentRoomId: null,
    });
    await update(presenceRef, {
      inGameRoom: false,
      lastSeen: Date.now(),
      roomId: null,
    });

    // useAuthStore.getState().setUserData({
    //   ...userData,
    //   currentRoomId: null,
    // });
    useRoomsStore.getState().clearMatch();
    useRoomsStore.getState().clearRoom();
    useRoomsStore.getState().clearPlayerData();

    setTimeout(() => {
      if (userData.test) {
        navigate("/");
      } else {
        navigate("/world");
      }
    }, 3000);
  };

  const showPrompt = () => {
    if (players[matchData.currentPlayerTurn]?.id === userData.id) {
      if (!playerMoveType) {
        return (
          <Box style={styles.promptContainer}>
            <Typography style={styles.promptText}>
              Your Turn: {cpMovesLeft} / 2
            </Typography>
          </Box>
        );
      }
      if (playerMoveType === "attack" && !selectedPlayer) {
        return (
          <Box style={styles.promptContainer}>
            <Typography style={styles.promptText}>
              SELECT A PLAYER TO ATTACK!
            </Typography>
          </Box>
        );
      } else if (playerMoveType === "playCard" && !message) {
        return (
          <Box style={styles.promptContainer}>
            <Typography style={styles.promptText}>
              SELECT A PLAYER TO APPLY!
            </Typography>
          </Box>
        );
      }
    } else {
      return (
        <Box style={styles.promptContainer}>
          <Typography style={styles.promptText}>
            Turn: {cpMovesLeft} / 2 <br /> Waiting for{" "}
            {players[matchData.currentPlayerTurn]?.name}...
          </Typography>
        </Box>
      );
    }
  };

  const showTimer = () => {
    const timer = matchData?.turnTime ?? 30;

    return (
      <Box
        sx={{
          position: "absolute",
          top: "1%",
          right: "1%",
          zIndex: 100,
        }}
      >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CircularProgress
            variant="determinate"
            value={Math.max(0, 100 - (elapsedSeconds / timer) * 100)}
            color="secondary"
            size={50}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              component="div"
              color="white"
              style={{ fontWeight: "bold" }}
            >
              {`${Math.max(0, timer - elapsedSeconds)}s`}
            </Typography>
          </Box>
          <img
            src={require("../@shared/assets/icons/time_icon.png")}
            alt=""
            style={{
              position: "absolute",
              top: "50%",
              left: "-20%",
              transform: "translate(0%, -50%)",
              width: "30px",
              height: "30px",
            }}
          />
        </Box>
      </Box>
    );
  };

  if (!matchData) return <Typography>Loading Match...</Typography>;

  const renderFooterButtons = () => {
    console.log("matchData.ranked", matchData.ranked);
    return (
      <Box
        sx={{
          flexDirection: "row",
          marginTop: "20px",
          gap: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {resultsPage !== "xp" && matchData?.ranked && (
          <NeoButton
            icon={<ArrowUpward size={30} color={Theme.palette.text.main} />}
            width={40}
            text="XP"
            color={Theme.palette.primary.main}
            onClick={() => setResultsPage("xp")}
          />
        )}

        <NeoButton
          text="Rematch"
          width={40}
          color={Theme.palette.secondary.main}
          onClick={() => console.log("Rematch")}
        />

        {!matchData?.ranked ? (
          <NeoButton
            text="Fin"
            width={40}
            color={Theme.palette.primary.main}
            onClick={() => handleEndMatch()}
          />
        ) : resultsPage === "xp" ? (
          <NeoButton
            text="Fin"
            width={40}
            color={Theme.palette.primary.main}
            onClick={() => {
              handleEndMatch();
            }}
          />
        ) : null}
      </Box>
    );
  };

  return (
    <Grid
      container
      xs={12}
      sx={{
        p: { xs: 0, md: 2 },
        height: "100%",
        width: "100%",
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          p: { xs: 0 },
        }}
      >
        {selectedCard && cardLayout && (
          <Box
            onClick={closeSelectedCard}
            style={{
              position: "absolute",
              position: "absolute",
              width: "100%",
              height: "100%",
              zIndex: 2500,
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <motion.div
              style={{
                position: "absolute",
                top: "40%",
                left: window.innerWidth >= 900 ? "36%" : "20%",
                width:
                  window.innerWidth >= 900
                    ? cardLayout.width * 3
                    : cardLayout.width,
                height:
                  window.innerWidth >= 900
                    ? cardLayout.height * 3
                    : cardLayout.width,
                zIndex: 1000,
              }}
              initial={{ opacity: 0, y: 300 }}
              animate={{ opacity: 1, y: -0 }}
              transition={{
                type: "spring",
                damping: 20,
                duration: 0.2,
                stiffness: 200,
                delay: 0.2,
              }}
            >
              <InfinityCard
                {...selectedCard}
                heightPer={100}
                // heightPer={window.innerWidth >= 900 ? 70 : 100}
                containerHeight={
                  window.innerWidth > 900
                    ? window.innerHeight * 0.38
                    : window.innerHeight * 0.38
                }
                borderPx={4}
              />
            </motion.div>
          </Box>
        )}

        <Grid
          item
          xs={12}
          md={7}
          sx={{
            backgroundColor: "#00000333",
            borderRadius: 2,
            height: "100%",
            padding: { xs: 0, md: 1 },
          }}
        >
          <Grid
            item
            container
            xs={12}
            sx={{
              ...styles.container,
              position: "relative",
              display: { xs: "flex" },
              border: { xs: "", md: "1px solid #ccc" },
              borderRadius: 2,
              height: "100%",
            }}
          >
            {!selectedPlayer && !message && showPrompt()}

            {showTimer()}

            {players && players.length > 0 && (
              <>
                {/* {toastMessage && (
                  <>
                    <ActionToast
                      message={toastMessage}
                      onHide={() => {
                        console.log("🧼 Toast onHide fired!");
                        resetToast();
                      }}
                      onComplete={() => {
                        console.log("Toast done showing...");
                      }}
                    />
                  </>
                )} */}
                <Toast
                  timeTilClose={2500} //in ms
                  onFinish={() => {
                    resetToast();
                  }}
                />

                <Box sx={styles.header}>
                  <NeoButton
                    isIcon={true}
                    icon={<MenuRounded />}
                    color={Theme.palette.tirciary.main}
                    width={100}
                    borderRadius={14}
                    onClick={() => setSettingsOpen(true)}
                  />
                </Box>

                {/* Game Board - Players map */}
                <Box sx={styles.playersContainer}>
                  {players.map((player, index) => (
                    <PlayerPlatform
                      key={index}
                      {...player}
                      playerTurn={
                        players[matchData.currentPlayerTurn]?.id === player.id
                      }
                      isSelected={selectedPlayer?.id === player.id}
                      onSelect={() => {
                        if (selectedPlayer?.id !== player.id) {
                          setSelectedPlayer(player);
                        } else {
                          setSelectedPlayer(null);
                        }

                        if (selectedCard) {
                          setShowPlayerMenu(true);
                        }
                      }}
                      // activeAnimation={() => console.log(activeAnimation)}
                      activeAnimation={activeAnimation}
                      cpMovesLeft={matchData?.cpMovesLeft}
                      // nextTurn={nextTurn}
                      playerMoveType={playerMoveType}
                      lastMove={matchData?.moves?.slice(-1)[0] || null}
                      coOp={matchData?.pve}
                    />
                  ))}
                </Box>

                {/* Game Board - Deck Stack */}
                {!attackAnimation && (
                  <DeckStack
                    deck={myDeck}
                    amtOfCardsToDraw={(() => {
                      if (!matchData) return 0;

                      if (
                        matchData?.currentPlayerTurn === 0 &&
                        matchData?.currentTurn === 1
                      ) {
                        //   console.log("CARDS DEBUGGER... we have 5 cards to draw");
                        return 5;
                      }

                      const last2moves = matchData?.moves?.slice(-2) || [];
                      const relatedMoves = last2moves.filter(
                        (move) =>
                          move.player === currentPlayer.name ||
                          move.attacker === currentPlayer.name
                      );

                      if (relatedMoves.length === 2) {
                        const playedCardsAmt = relatedMoves.filter(
                          (move) => move.type === "playCard"
                        ).length;
                        return playedCardsAmt;
                      }

                      return 0;
                    })()}
                    onFinishDrawAnimation={() => setFinishedDrawAnimation(true)}
                  />
                )}

                {/* Game Board - Player Section */}
                {selectedPlayer && (
                  <motion.div
                    key={selectedPlayer.id}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      position: "absolute",
                      top: "0%",
                      left: 0,
                      zIndex: 100,
                      height: "30%",
                      width: "100%",
                    }}
                  >
                    <CurrentPlayer
                      {...selectedPlayer}
                      selectCard={selectCard}
                      isCurrentPlayer={false}
                    />
                  </motion.div>
                )}

                {userData && currentPlayer && (
                  <CurrentPlayer
                    {...currentPlayer}
                    hand={myHand}
                    selectCard={selectCard}
                    isCurrentPlayer={true}
                    playerTurn={
                      players[matchData.currentPlayerTurn]?.id === userData.id
                    }
                    updatePlayerStance={updatePlayerStance}
                    setPlayerMoveType={setPlayerMoveType}
                    setSelectedPlayer={setSelectedPlayer}
                    handDisabled={
                      selectedPlayer !== null ||
                      selectedCard !== null ||
                      disabledByToast ||
                      matchData?.cpMovesLeft === 0
                    }
                    attackAnimation={attackAnimation}
                    disabledByToast={disabledByToast}
                    finishedDrawAnimation={finishedDrawAnimation}
                  />
                )}

                {isMyTurn && selectedCard && cardLayout && playerMove && (
                  //  Box containing Button options for the selected card
                  <Box sx={styles.cardOptionsContainer}>
                    {/* TRADE CARD BUTTON */}
                    {!selectedCard.isStone && (
                      <Box
                        style={{
                          ...styles.moveButton,
                          flexDirection: "column",
                          width: 42,
                          marginBottom: 0,
                        }}
                      >
                        <NeoButton
                          icon={
                            <img
                              src={require("../@shared/assets/icons/trade_card_icon.png")}
                              style={{ width: 30, height: 30 }}
                              alt=""
                            />
                          }
                          isIcon={true}
                          onClick={() => {
                            setPlayerMoveType("tradeCard");
                            setCardLayout(null);
                            setShowPlayerMenu(true);
                          }}
                          width={100}
                          color={Theme.palette.primary.main}
                        />
                        <Typography style={styles.cardOptionText}>
                          Trade It
                        </Typography>
                      </Box>
                    )}

                    {/* PLAY CARD BUTTON */}
                    {!selectedCard.isStone && (
                      <Box
                        style={{
                          ...styles.moveButton,
                          flexDirection: "column",
                          width: 42,
                          marginBottom: 0,
                        }}
                      >
                        <NeoButton
                          icon={
                            <img
                              src={require("../@shared/assets/icons/play_card_icon.png")}
                              style={{ width: 30, height: 30 }}
                              alt=""
                            />
                          }
                          isIcon={true}
                          onClick={() => {
                            setPlayerMoveType("playCard");
                            setCardLayout(null);
                            setShowPlayerMenu(true);
                          }}
                          width={100}
                          color={Theme.palette.primary.main}
                        />
                        <Typography style={styles.cardOptionText}>
                          Play It
                        </Typography>
                      </Box>
                    )}
                    {/* DISCARD CARD BUTTON */}

                    <Box
                      style={{
                        ...styles.moveButton,
                        flexDirection: "column",
                        width: 42,
                        marginBottom: 0,
                      }}
                    >
                      <NeoButton
                        icon={
                          <img
                            src={require("../@shared/assets/icons/discard_icon.png")}
                            style={{ width: 30, height: 30 }}
                            alt=""
                          />
                        }
                        isIcon={true}
                        onClick={() => {
                          handleCardAction("discardCard");
                        }}
                        width={100}
                        color={Theme.palette.secondary.main}
                      />
                      <Typography style={styles.cardOptionText}>
                        Discard It
                      </Typography>
                    </Box>
                  </Box>
                )}

                {isMyTurn && playerMoveType === "attack" && selectedPlayer && (
                  <motion.div
                    initial={{ opacity: 0, y: -40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      position: "absolute",
                      bottom: "-15%",
                      left: 0,
                      zIndex: 1000,
                      width: "100%",
                    }}
                  >
                    <SpinWheel
                      numberSpun={numberSpun}
                      setNumberSpun={setNumberSpun}
                    />
                  </motion.div>
                )}

                {attackAnimation && matchData && (
                  <FightAnimation
                    attackAnimation={attackAnimation}
                    character={
                      players?.find(
                        (player) =>
                          player.name ===
                          matchData?.moves[matchData?.moves?.length - 1]
                            .attacker
                      )?.character
                    }
                    onFinish={() => setAttackAnimation(null)}
                  />
                )}

                {activeCardPlayAnimation && (
                  <SlapCardAnimation
                    card={activeCardPlayAnimation.card}
                    onFinish={() => {
                      const targetPlayer = players.find(
                        (p) => p.name === activeCardPlayAnimation.target
                      );

                      if (targetPlayer) {
                        setActiveAnimation({
                          type: "playCard",
                          target: targetPlayer.name,
                          timestamp: activeCardPlayAnimation.timestamp,
                          fps: 18,
                        });
                      }

                      // 💥 Clean it up
                      setActiveCardPlayAnimation(null);
                    }}
                  />
                )}

                {showTradeAnimation && (
                  <TradeCardAnimation
                    onFinish={() => {
                      setShowTradeAnimation(false);
                    }}
                  />
                )}

                {showResultsModal && (
                  <>
                    {/* Overlay Layer */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "#00000090", // translucent dark overlay
                        zIndex: 9998,
                      }}
                    />

                    {/* Modal Layer */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        alignItems: "center",
                        zIndex: 9999,
                        backgroundColor: "#16116e",
                        borderRadius: 10,
                        width: "90%",
                        height: { xs: "50%", md: "90%" },
                        padding: "20px",
                        justifyContent: "space-between",
                        margin: 0,
                      }}
                    >
                      {resultsPage === "placement" ? (
                        <>
                          <Typography
                            style={{
                              fontSize: 28,
                              fontWeight: "bold",
                              color: "#fff",
                              marginBottom: "20px",
                              textAlign: "center",
                            }}
                          >
                            🎉{" "}
                            {matchData?.pve
                              ? matchData?.players.find(
                                  (p) =>
                                    p.id === matchData?.winner.id && !p.isBot
                                )
                                ? "Humans"
                                : "Bots"
                              : matchData?.winner?.name}{" "}
                            Wins! 🎉
                          </Typography>

                          {/* Placement List */}
                          <Box
                            sx={{
                              // flex: 1,
                              width: "100%",
                              overflowY: "auto",
                              maxHeight: "75%",
                            }}
                          >
                            <Box
                              sx={{
                                flexDirection: "row",
                                flexWrap: "wrap",
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              {matchData?.players
                                ?.map((p) => {
                                  const eliminationIndex =
                                    matchData.eliminated.findIndex(
                                      (e) => e.id === p.id
                                    );
                                  return {
                                    ...p,
                                    placement:
                                      p.id === matchData.winner.id
                                        ? 1
                                        : eliminationIndex + 2 || 99,
                                  };
                                })
                                .sort((a, b) => a.placement - b.placement)
                                .map((player, index) => (
                                  <PlayerResultCard
                                    key={player.id}
                                    player={player}
                                    placement={player.placement}
                                    matchData={matchData}
                                  />
                                ))}
                            </Box>
                          </Box>

                          {renderFooterButtons()}
                        </>
                      ) : (
                        <>
                          <XPBreakdown
                            userData={userData}
                            roomId={roomId}
                            allPlayersXP={allPlayersXP}
                            playerPoints={playerPoints}
                          />
                          {renderFooterButtons()}
                        </>
                      )}
                    </Box>
                  </>
                )}

                {/* ACTION BUTTON ===================== */}
                {players[matchData.currentPlayerTurn]?.id === userData.id &&
                  !players.find((p) => p.id === userData.id)?.character
                    ?.isDead && (
                    <Box
                      style={{
                        position: "absolute",
                        bottom: 15,
                        right: 15,
                        zIndex: 9998,
                      }}
                    >
                      <NeoButton
                        isIcon={true}
                        disabled={
                          !players[matchData.currentPlayerTurn]?.id ===
                          userData.id
                        }
                        icon={
                          <img
                            src={require("../@shared/assets/icons/fist_icon.png")}
                            style={{ width: 30, height: 30 }}
                          />
                        }
                        color={Theme.palette.secondary.main}
                        onClick={() => {
                          setShowPlayerMenu(true);
                          setSelectedPlayer(null);
                        }}
                      />
                    </Box>
                  )}

                {/* PLAYER ACTION MENU ===================== */}
                {showPlayerMenu && (
                  <Box sx={styles.moveSelectionContainer}>
                    {playerMove && (
                      <>
                        {!selectedCard &&
                          ((currentPlayer?.character?.stance === "attack" &&
                            currentPlayer?.character?.defense !== 0) ||
                            currentPlayer?.character?.stance === "defense") && (
                            <Box sx={styles.moveButton}>
                              <NeoButton
                                text={"Change To"}
                                onClick={() => {
                                  updatePlayerStance();
                                  setShowPlayerMenu(false);
                                }}
                                width={100}
                                color={Theme.palette.primary.main}
                                endAdorn={
                                  <img
                                    src={
                                      currentPlayer?.character?.stance ===
                                      "defense"
                                        ? require("../@shared/assets/icons/swords_icon2.png")
                                        : require("../@shared/assets/icons/sheild.png")
                                    }
                                    style={styles.moveIcon}
                                  />
                                }
                              />
                            </Box>
                          )}

                        {isMyTurn &&
                          currentPlayer?.character?.stance === "attack" &&
                          !selectedCard && (
                            <Box sx={styles.moveButton}>
                              <NeoButton
                                text="Attack"
                                onClick={() => {
                                  setPlayerMoveType("attack");
                                  setShowPlayerMenu(false);
                                }}
                                width={100}
                                color={Theme.palette.primary.main}
                                startAdorn={
                                  <img
                                    src={require("../@shared/assets/icons/swords_icon2.png")}
                                    style={styles.moveIcon}
                                  />
                                }
                              />
                            </Box>
                          )}

                        {((selectedPlayer && playerMoveType === "playCard") ||
                          (selectedPlayer && playerMoveType === "tradeCard") ||
                          (selectedPlayer && playerMoveType === "attack")) && (
                          <>
                            <Box sx={styles.moveButton}>
                              <NeoButton
                                icon={<Check size={30} color="$text" />}
                                text="Confirm"
                                onClick={() => {
                                  handleCardAction(playerMoveType);
                                }}
                                width={100}
                                color={Theme.palette.primary.main}
                              />
                            </Box>
                          </>
                        )}
                      </>
                    )}

                    <Box sx={styles.moveButton}>
                      <NeoButton
                        icon={<DoNotDisturb size={30} color="#000" />}
                        text="Cancel"
                        onClick={() => {
                          setSelectedPlayer(null);
                          setSelectedCard(null);
                          setShowPlayerMenu(false);
                          setSelectedCard(null);
                          setPlayerMoveType(null);
                        }}
                        width={100}
                        color={Theme.palette.secondary.main}
                      />
                    </Box>
                  </Box>
                )}

                {/* CHAT BUTTON ===================== */}
                {!chatOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "15px",
                      left: "15px",
                      zIndex: 9998,
                    }}
                  >
                    <Box
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                      }}
                    >
                      {hasUnreadMessages && <Box sx={styles.unread} />}
                      {/* <Badge
                        badgeContent={unreadCount > 0 ? unreadCount : null}
                        color="error"
                        sx={{
                          "& .MuiBadge-badge": {
                            top: 2,
                            right: 2,
                            border: "2px solid #000",
                            fontSize: "0.75rem",
                            height: "16px",
                            minWidth: "16px",
                          },
                        }}
                      > */}
                      <NeoButton
                        isIcon={true}
                        icon={<Sms size={30} color={Theme.palette.text.main} />}
                        color={Theme.palette.primary.main}
                        onClick={() => {
                          setChatOpen(true);
                          setChatDrawerOpen(true); // ✅ open
                          clearUnreadMessages();
                        }}
                      />
                      {/* </Badge> */}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>

        <Grid
          item
          xs={4}
          lg={2}
          sx={{
            display: { xs: "none", md: "none", lg: "block" },
            backgroundColor: "#00000333",
            height: "100%",
            padding: 1,
          }}
        >
          <Grid
            item
            container
            xs={12}
            sx={{
              ...styles.container,
              position: "relative",
              display: { xs: "flex" },
              border: "1px solid #ccc",
              borderRadius: 2,
              maxHeight: "100%",
              overflowY: "scroll",
              scrollbarWidth: "none",
            }}
          >
            <Typography
              variant="caption"
              component="h1"
              //transparent background
              sx={styles.text}
            >
              Move Tracker
            </Typography>

            <Grid
              item
              container
              xs={12}
              sx={{
                padding: 1,
                overflowY: "scroll",
                scrollbarWidth: "none",
                maxHeight: "calc(100% - 35px)",
              }}
            >
              {matchData &&
                matchData?.moves &&
                [...matchData?.moves]
                  ?.sort((a, b) => b.timestamp - a.timestamp)
                  .map((move, idx) => {
                    const movePlayer = move.attacker ?? move.player;
                    const targetPlayer = move.target
                      ? move.target
                      : move.defender;

                    const formattedMessage = () => {
                      if (move.type === "attack") {
                        return `attacked ${targetPlayer} for ${move.damage} damage.`;
                      }
                      if (move.type === "attack - missed") {
                        return `tried to attack ${targetPlayer} but missed.`;
                      }
                      if (move.type === "playCard") {
                        return `applied a ${move.card.name} on ${
                          targetPlayer === movePlayer
                            ? "themselves"
                            : move.target
                        }.`;
                      }
                      if (move.type === "turnLoss") {
                        return `was affected by a turn loss.`;
                      }
                      if (move.type === "tradeCard") {
                        return `traded a card with ${targetPlayer}.`;
                      }
                      if (move.type === "discardCard") {
                        return `discarded a card from thier hand.`;
                      }
                      if (move.type === "stanceChange") {
                        return `changed their stance to ${move.newStance}.`;
                      }
                      if (move.type === "playerSkipped") {
                        return `was skipped by the timer.`;
                      }
                    };

                    return (
                      <Grid
                        item
                        container
                        xs={12}
                        sx={{
                          width: "100%",
                          justifyContent: "space-between",
                          alignItems: "center",
                          border: "1px solid #fff",
                          background: `${Theme.palette.primary.dark}80`,
                          borderRadius: 1,
                          mb: 1,
                          padding: 0.5,
                        }}
                        key={idx}
                      >
                        <Box
                          style={{
                            width: 50,
                            height: 50,
                          }}
                        >
                          <img
                            src={
                              players?.find(
                                (p) =>
                                  p.name === move.player ||
                                  p.name === move.attacker
                              )?.character?.imageUrl
                            }
                            style={{
                              height: "100%",
                              width: "100%",
                              objectFit: "contain",
                            }}
                            alt={""}
                          />
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "#fff",
                            width: "calc(100% - 60px)",
                            fontSize: 12,
                          }}
                        >
                          {formattedMessage()}
                        </Typography>
                        <Grid
                          item
                          container
                          xs={12}
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="h1"
                            sx={{
                              color: "#fff",
                              fontWeight: "bold",
                              fontSize: 12,
                            }}
                          >
                            {movePlayer}
                          </Typography>

                          <Typography
                            variant="caption"
                            component="h1"
                            sx={{
                              color: "#fff",
                              fontSize: 12,
                            }}
                          >
                            {new Date(move.timestamp).toLocaleTimeString()}
                          </Typography>
                        </Grid>
                      </Grid>
                    );
                  })}
            </Grid>
          </Grid>
        </Grid>

        <ChatDrawer
          userData={userData}
          roomId={roomId}
          visible={chatOpen}
          onClose={() => {
            setChatOpen(false);
            setChatDrawerOpen(false); // ✅ close
          }}
        />

        <SettingsDrawer
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          screen={"gameboard"}
          onclick={() => handleQuitMatch()}
        />
      </Container>
    </Grid>
  );
};

const styles = {
  container: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  header: {
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 9999,
    margin: "10px",
    width: 50,
  },
  text: { color: "#fff", fontWeight: "bold" },
  playersContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-evenly",
    transform: "skewX(-50deg)",
    width: { xs: "50%", md: "50%" },
    marginRight: { xs: "15%", md: "7%" },
    marginBottom: { xs: "0%", md: "10%" },
  },
  cardOptionsContainer: {
    zIndex: 2500,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "center",
    width: "100%",
    height: "20%",
    backgroundColor: "#00000090",
    padding: "10px",
    borderRadius: "10px",
    position: "absolute",
    bottom: "0%",
    left: "50%",
    transform: "translateX(-50%)",
  },
  cardOptionText: {
    fontSize: "12px",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
  },
  moveSelectionContainer: {
    zIndex: 9999,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "fit-content",
    height: "auto",
    backgroundColor: "#00000050",
    padding: "5px",
    borderRadius: "10px",
    position: "absolute",
    bottom: "25px",
    right: "10px",
  },
  moveButton: {
    width: "200px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "10px",
  },
  moveIcon: {
    width: "24px",
    height: "24px",
    marginLeft: "10px",
  },
  moveText: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  unread: {
    position: "absolute",
    top: 3,
    right: 0,
    backgroundColor: "#FF0000",
    padding: "5px",
    borderRadius: "10px",
    borderWidth: "4px",
    borderColor: "#000",
    color: "#FFFFFF",
    width: "12px",
    height: "12px",
    zIndex: 1000,
  },
  promptContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#00000050",
    color: "#fff",
    padding: 20,
    borderRadius: 10,
    position: "absolute",
    top: "15%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 9999,
  },
  promptText: {
    fontSize: 18,
    fontWeight: 900,
    marginBottom: 10,
    color: "#fff",
    textAlign: "center",
  },
};

export default GameBoardScreen;
